import { morphSection } from '@theme/section-renderer';

document.addEventListener("DOMContentLoaded", init);

function init() {
  initiateAddlFGPromotion(true);
}

/**
 * MB Free Gift Promotions
 * Automatically adds/removes free gift add-on products based on cart contents and promotion rules.
 * @param {boolean} onLoad - whether the function is being called on page load
 * @returns {Promise<void|Array>} - returns an array of items to remove if not on load, otherwise updates the cart UI
 */
export async function initiateAddlFGPromotion(onLoad = false) {
  // Fetch current cart
  let cartItems, addonAttributes;
  let updatedCartSection = null;

  const cartSectionId = document.querySelector("cart-items-component")?.dataset.sectionId;
  const cart = await fetch("/cart.js").then((r) => r.json());

  cartItems = cart.items;
  addonAttributes = cart.attributes["_product_addon_qualifier"];

  // Get all cart item IDs currently in cart
  const cartItemIds = cartItems.map((item) => item.id);
  const toAdd = []; // variant IDs to add
  const toRemove = []; // line item keys to remove

  for (const rule of window.addonRules) {
    const { qualifyingIds, addonVariantId, priority } = rule;

    // Check if any qualifying product is in cart
    const hasQualifying = qualifyingIds.some((id) => cartItemIds.includes(id));

    // Check if the gift is already in cart
    const giftLineItem = cartItems.find((item) => item.id === addonVariantId  &&  item.properties?._is_addon_product === true);
    const giftInCart = !!giftLineItem;

    if (hasQualifying && !giftInCart) {
      // Add the gift
      toAdd.push({
        id: addonVariantId,
        quantity: 1,
        properties: { _is_addon_product: true },
      });
    } else if (!hasQualifying && giftInCart) {
      const qualifyingItems = cartItems.filter((item) => item.id === giftLineItem.id && item.properties?._is_addon_product === true);
      qualifyingItems.forEach(item => {
        toRemove.push({ key: item.key, quantity: 0});
      });
    }
  }

  if (onLoad) {
    let orphanedFGs = filterOrphanedFreeGifts(cartItems);
    if(orphanedFGs.length > 0) { 
      orphanedFGs.forEach(freeGift => {
        const alreadyExists = toRemove.some( item => item.key === freeGift.key ); // check for duplicates

        if (!alreadyExists) {
          toRemove.push({ key: freeGift.key, quantity: 0 });
        }
      })
    }
  }

  // cart updates
  if (toRemove.length > 0) {
    if(!onLoad) {
        return toRemove;
    } else {
      try {
        const response = await fetch("/cart/update.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
              updates: Object.fromEntries(toRemove.map((i) => [i.key, 0])),
              sections: cartSectionId,
          }),
        });

        if (!response.ok) throw new Error("Remove from cart failed");
        const data = await response.json();
        updatedCartSection = data.sections[cartSectionId];
      } catch (error) {
        console.error('mb-free-gift-promotions: error', error);
      }    
    }
  }

  else if (toAdd.length > 0) {
    const payload = {
      items: toAdd,
      sections: cartSectionId
    }

    payload["attributes"] =  {
      _product_addon_qualifier: window.addonRules,
    }
    
    try {
      const response = await fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error("Add to cart failed");
      const data = await response.json();
      updatedCartSection = data.sections[cartSectionId];
    } catch (error) {
      console.error('mb-free-gift-promotions: error ', error);
    }
  }

  // Refresh cart UI if items changed
  if (toAdd.length > 0 || toRemove.length > 0) {
    morphSection(cartSectionId, updatedCartSection);
  }
}

/**
 * Returns free gift items whose qualifying product is no longer present in the cart.
 *
 * @param {Array<Object>} cartItems
 * @returns {Array<Object>} Orphaned free gift items.
 */
export function filterOrphanedFreeGifts(cartItems) {
  return cartItems.filter(freeGift => {
    // Check if the item is a free gift and has a qualifying product key
    if (freeGift.properties && isTrue(freeGift.properties._free_gift) && freeGift.properties._qualifying_line_item_id) {
      const qualifyingKey = freeGift.properties._qualifying_line_item_id;

      // Check if there's NO corresponding product with a matching _line_item_id
      const hasMatchingProduct = cartItems.some(product =>
        product.properties &&
        product.properties._line_item_id === qualifyingKey
      );
      return !hasMatchingProduct; // Keep only if no matching product exists
    }
    return false; // Exclude non-free-gift items
  });
};

function isTrue(value) {
  return value === true || value === "true";
}

window.initiateAddlFGPromotion = initiateAddlFGPromotion;